import { Badge, Table, Tbody, Td, Th, Thead, Tooltip, Tr } from "@chakra-ui/react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { FieldViewModel } from "./field.view-model";
import { EditButton } from "components/elements/edit-button/edit-button";
import { EmptyListMessage } from "components/elements/empty-list-message/empty-list-message";
import { fieldTypes } from "./field-types";
import { checkUiConfiguration } from "utils/uiconfighelper";
import { useTenant } from "modules/tenant/providers/tenant-provider";
import { ThDeveloperMode } from "pages/Settings/developer-mode/th-developer-mode";
import { TdDeveloperMode } from "pages/Settings/developer-mode/td-developer-mode";

interface FieldTableProps {
  fields: FieldViewModel[];
  onOpen: (field: FieldViewModel) => void;
}

export function FieldTable({ fields, onOpen }: FieldTableProps) {
  const { user } = useTenant();

  const applicationTerm =
    user?.company?.id === "2e6eae2b-de55-4c49-96c6-ff77740565f1" ||
    user?.company?.id === "59d63802-e33d-4b99-9ddd-e92004455eb3"
      ? "Term Sheet"
      : "Application";

  function showOnApplication(field: FieldViewModel) {
    return field.show_on_lender_application || field.show_on_borrower_application;
  }

  function showOnLoan(field: FieldViewModel) {
    return field.show_on_lender_loan || field.show_on_borrower_loan;
  }

  function isLoanSectionEnabled() {
    return checkUiConfiguration(user, "loan_section");
  }

  return (
    <>
      {fields.length === 0 ? (
        <EmptyListMessage />
      ) : (
        <Table variant="simple">
          <Thead>
            <Tr>
              <Th>
                <Tooltip label="Loan field name" hasArrow>
                  Label
                </Tooltip>
              </Th>
              <Th>
                <Tooltip label="Type of data" hasArrow>
                  Type
                </Tooltip>
              </Th>

              <ThDeveloperMode>Data Key</ThDeveloperMode>
              <ThDeveloperMode>Identifier Key</ThDeveloperMode>
              <ThDeveloperMode>Calculated</ThDeveloperMode>

              <Th textAlign="center">
                <Tooltip label="This field is required to submit the loan" hasArrow>
                  Required
                </Tooltip>
              </Th>

              {!isLoanSectionEnabled() && (
                <>
                  <Th textAlign="center">
                    <Tooltip label="This field will be editable" hasArrow>
                      Editable
                    </Tooltip>
                  </Th>

                  <Th textAlign="center">
                    <Tooltip label="This field will be used in underwriting" hasArrow>
                      Underwriting
                    </Tooltip>
                  </Th>
                </>
              )}

              <Th textAlign="center">
                <Tooltip
                  label={`Display this field on the loan ${applicationTerm.toLowerCase()} form`}
                  hasArrow
                >
                  {applicationTerm}
                </Tooltip>
              </Th>
              <Th textAlign="center">
                <Tooltip label="Display this field on the loan form" hasArrow>
                  Loan
                </Tooltip>
              </Th>
              <Th></Th>
            </Tr>
          </Thead>
          <Tbody>
            {fields
              ?.sort((a: FieldViewModel, b: FieldViewModel) => a.order - b.order)
              .map((field: FieldViewModel) => (
                <Tr key={field.id}>
                  {/* FIELD LABEL */}
                  <Td wordBreak="break-word">{field.label}</Td>

                  {/* FIELD TYPE */}
                  <Td>
                    <Badge variant="neutral" size="xs">
                      {fieldTypes[field.type]}
                    </Badge>
                  </Td>

                  {/* DEVELOPER MODE */}
                  <TdDeveloperMode>{field.data_key || "-"}</TdDeveloperMode>
                  <TdDeveloperMode>{field.identifier_key || "-"}</TdDeveloperMode>
                  <TdDeveloperMode textAlign="center">
                    {field.calculated && <FontAwesomeIcon icon={["fas", "check"]} />}
                  </TdDeveloperMode>
                  {/* / DEVELOPER MODE */}

                  {/* FIELD REQUIRED */}
                  <Td textAlign="center">
                    {field.required && <FontAwesomeIcon icon={["fas", "check"]} />}
                  </Td>

                  {!isLoanSectionEnabled() && (
                    <>
                      {/* FIELD EDITABLE */}
                      <Td textAlign="center">
                        {field.editable && <FontAwesomeIcon icon={["fas", "check"]} />}
                      </Td>

                      {/* FIELD UNDERWRITING */}
                      <Td textAlign="center">
                        {field.underwriting && <FontAwesomeIcon icon={["fas", "check"]} />}
                      </Td>
                    </>
                  )}

                  {/* FIELD SHOW ON APPLICATION */}
                  <Td textAlign="center">
                    {showOnApplication(field) && <FontAwesomeIcon icon={["fas", "check"]} />}
                  </Td>

                  {/* FIELD SHOW ON LOAN */}
                  <Td textAlign="center">
                    {showOnLoan(field) && <FontAwesomeIcon icon={["fas", "check"]} />}
                  </Td>

                  {/* FIELD ACTIONS */}
                  <Td isNumeric pr="0px">
                    <EditButton onClick={() => onOpen(field)} />
                  </Td>
                </Tr>
              ))}
          </Tbody>
        </Table>
      )}
    </>
  );
}
